import React from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { StoreUser } from '../../../../services/authService';
import type { StoreDetailsSendNotificationFormValues } from '../../../../types/features/storeDetails/notifications';
import AdminFormRow from '../../../../components/admin/AdminFormRow';
import { adminColors } from '../../../../styles/adminDesignTokens';
import {
    adminButtonSx,
    adminButtonToneSx,
    adminCheckboxSx,
    adminCombineSx,
    adminDialogActionsSx,
    adminDialogContentSx,
    adminDialogSx,
    adminDialogTitleSx,
    adminInlineControlLabelSx,
    adminFormControlSx,
    adminFormFieldSx,
} from '../../../../styles/adminComponentStyles';
import { STORE_DETAILS_NOTIFICATION_CHANNEL_OPTIONS } from '../utils/storeDetailsNotificationConstants';

type StoreDetailsSendNotificationDialogProps = {
    open: boolean;
    users: StoreUser[];
    values: StoreDetailsSendNotificationFormValues;
    submitting: boolean;
    sendDisabled: boolean;
    error: string;
    onClose: () => void;
    onSend: (event?: React.FormEvent<HTMLFormElement>) => void;
    onTitleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onMessageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onAuditIdChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onCriticalChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onUserChange: (event: SelectChangeEvent<string[]>) => void;
    onChannelChange: (event: SelectChangeEvent) => void;
};

const StoreDetailsSendNotificationDialog: React.FC<StoreDetailsSendNotificationDialogProps> = ({
    open,
    users,
    values,
    submitting,
    sendDisabled,
    error,
    onClose,
    onSend,
    onTitleChange,
    onMessageChange,
    onAuditIdChange,
    onCriticalChange,
    onUserChange,
    onChannelChange,
}) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
            sx: adminCombineSx(adminDialogSx, { maxWidth: '570px' }),
        }}
    >
        <DialogTitle
            sx={adminCombineSx(adminDialogTitleSx, {
                backgroundColor: 'rgb(239, 246, 255)',
                color: adminColors.primary,
                fontWeight: 600,
                marginBottom: 0,
            })}
        >
            Send Notification
        </DialogTitle>
        <DialogContent sx={adminCombineSx(adminDialogContentSx, { p: '20px', pt: '20px !important' })}>
            {error && (
                <Typography color="error" sx={{ mb: 1.5, fontSize: 14 }}>
                    {error}
                </Typography>
            )}
            <Stack
                id="store-details-send-notification-form"
                component="form"
                spacing={2}
                onSubmit={onSend}
            >
                <AdminFormRow label="Title" required>
                    <TextField value={values.title} onChange={onTitleChange} fullWidth sx={adminFormFieldSx} />
                </AdminFormRow>
                <AdminFormRow label="Message" required>
                    <TextField
                        value={values.message}
                        onChange={onMessageChange}
                        fullWidth
                        multiline
                        minRows={4}
                        sx={adminFormFieldSx}
                    />
                </AdminFormRow>
                <AdminFormRow label="Audit ID">
                    <TextField value={values.auditId} onChange={onAuditIdChange} fullWidth sx={adminFormFieldSx} />
                </AdminFormRow>

                <AdminFormRow label="User" required>
                    <FormControl fullWidth sx={adminFormControlSx}>
                    <Select
                        multiple
                        value={values.userIds}
                        displayEmpty
                        onChange={onUserChange}
                        renderValue={(selected) => {
                            const selectedUsers = users.filter((user) => selected.includes(user.user_id));
                            if (selectedUsers.length === 0) {
                                return 'Select users';
                            }
                            return selectedUsers
                                .map((user) => user.display_name || user.contact_email || user.user_id)
                                .join(', ');
                        }}
                    >
                        {users.length === 0 ? (
                            <MenuItem value="" disabled>
                                No users available
                            </MenuItem>
                        ) : (
                            users.map((user) => (
                                <MenuItem
                                    key={user.user_id}
                                    value={user.user_id}
                                    sx={{ alignItems: 'center', gap: 1 }}
                                >
                                    <Checkbox
                                        checked={values.userIds.includes(user.user_id)}
                                        sx={adminCombineSx(adminCheckboxSx, {
                                            mr: 0.5,
                                            '& .MuiSvgIcon-root': {
                                                fontSize: 20,
                                            },
                                        })}
                                    />
                                    <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                                        <Typography sx={{ color: '#222b45', fontSize: 15 }}>
                                            {user.display_name || user.contact_email || user.user_id}
                                        </Typography>
                                        <Typography sx={{ color: '#6b7280', fontSize: 12, fontWeight: 500 }}>
                                            ({user.role})
                                        </Typography>
                                    </Stack>
                                </MenuItem>
                            ))
                        )}
                    </Select>
                    </FormControl>
                </AdminFormRow>

                <AdminFormRow label="Channel" required>
                    <FormControl fullWidth sx={adminFormControlSx}>
                        <Select value={values.channel} onChange={onChannelChange}>
                            {STORE_DETAILS_NOTIFICATION_CHANNEL_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </AdminFormRow>

                <AdminFormRow label="Critical message">
                    <FormControlLabel
                        control={<Checkbox checked={values.critical} onChange={onCriticalChange} sx={adminCheckboxSx} />}
                        label="Enabled"
                        sx={adminInlineControlLabelSx}
                    />
                </AdminFormRow>
            </Stack>
        </DialogContent>
        <DialogActions sx={adminDialogActionsSx}>
            <Button
                onClick={onClose}
                variant="outlined"
                disabled={submitting}
                sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { height: 40 })}
            >
                Cancel
            </Button>
            <Button
                type="submit"
                form="store-details-send-notification-form"
                variant="contained"
                disabled={sendDisabled}
                sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { height: 40 })}
            >
                {submitting ? 'Sending...' : 'Send'}
            </Button>
        </DialogActions>
    </Dialog>
);

export default React.memo(StoreDetailsSendNotificationDialog);
